import { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { useAppContext } from "../contexts/AppContext";
import SignOutButton from "./SignOutButton";
import Navbar from "../components/Header/Navbar";
import MobileNavbar from "../components/Header/MobileNavbar";
import MenuIcon from "/menu.svg";

const Header = () => {
  const { isLoggedIn, user, isLoading } = useAppContext();
  const [displayName, setDisplayName] = useState<string>("User");
  const [isDesktop, setIsDesktop] = useState(window.innerWidth > 768);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false);
  const [hoverTimeout, setHoverTimeout] = useState<NodeJS.Timeout | null>(null);
  const [stayTimeout, setStayTimeout] = useState<NodeJS.Timeout | null>(null);
  const location = useLocation();
  const dropdownRef = useRef<HTMLDivElement>(null);

  const updateMedia = () => {
    setIsDesktop(window.innerWidth > 768);
  };

  useEffect(() => {
    if (isLoggedIn && user) {
      setDisplayName(`${user.firstName || "User"} ${user.lastName ? user.lastName.charAt(0) : ""}`);
    } else {
      setDisplayName("User");
    }
  }, [isLoggedIn, user]);


  useEffect(() => {
    window.addEventListener("resize", updateMedia);
    return () => window.removeEventListener("resize", updateMedia);
  }, []);

  const toggleProfileMenu = () => {
    setIsProfileMenuOpen((prev) => !prev);
  };

  const handleClickOutside = (event: MouseEvent) => {
    if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
      setIsProfileMenuOpen(false);
    }
  };

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    setIsProfileMenuOpen(false);
  }, [location]);

  const handleMouseEnter = () => {
    if (hoverTimeout) {
      clearTimeout(hoverTimeout);
    }
    const timeout = setTimeout(() => {
      setIsProfileMenuOpen(true);
    }, 30000);
    setHoverTimeout(timeout);
  };

  const handleMouseLeave = () => {
    if (hoverTimeout) {
      clearTimeout(hoverTimeout);
    }
    const timeout = setTimeout(() => {
      setIsProfileMenuOpen(false);
    }, 5000);
    setStayTimeout(timeout);
  };

  const handleDropdownMouseEnter = () => {
    if (stayTimeout) {
      clearTimeout(stayTimeout);
    }
  };

  const handleDropdownMouseLeave = () => {
    setIsProfileMenuOpen(false);
  };

  const handleLinkClick = (path: string) => {
    if (location.pathname !== path) {
      setIsProfileMenuOpen(false);
    }
  };

  const getRandomColor = () => {
    const randomColor = `#${Math.floor(Math.random() * 16777215).toString(16).padStart(6, '0')}`;
    return randomColor;
  };

  const getStoredColor = () => {
    const storedColor = localStorage.getItem('backgroundColor');
    return storedColor ? storedColor : getRandomColor();
  };

  const [backgroundColor, setBackgroundColor] = useState(getStoredColor);

  useEffect(() => {
    if (isLoggedIn && user && !localStorage.getItem('backgroundColor')) {
      const newColor = getRandomColor();
      setBackgroundColor(newColor);
      localStorage.setItem('backgroundColor', newColor);
    }
  }, [isLoggedIn, user]);

  const handleSignOut = () => {
    localStorage.removeItem('backgroundColor');
    // ...existing sign out logic...
  };

  return (
    <div style={{
      backgroundColor: 'white',
      width: '100%',
      height: '4rem',
      top: 0,
      zIndex: 40,
      position: 'fixed',
      boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
    }}>
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '0.75rem',
      }}>
        {/* Logo */}
        <Link to="/">
          <img
            className="halo-logo"
            src="/new-halo-logo.svg"
            alt="Halo Holidays logo"
          />
        </Link>

        {/* Mobile Menu Toggle */}
        <div className="md:hidden">
          {isLoading ? (
            <div className="animate-pulse w-8 h-8 rounded-full bg-gray-300"></div> // Loading placeholder
          ) : isLoggedIn ? (
            <div className="relative"
              onMouseEnter={handleMouseEnter}
              onMouseLeave={handleMouseLeave}
            >
              <button
                onClick={toggleProfileMenu}
                className="flex items-center space-x-2 focus:outline-none ml-[7rem]"
              >
                <div className="relative flex items-center justify-center w-8 h-8 rounded-full text-white font-medium text-lg avatar" style={{ backgroundColor }}>
                  {displayName?.charAt(0) || "U"}
                  <span className="absolute top-0 right-0 w-2.5 h-2.5 bg-green-500 border-2 border-white rounded-full"></span>
                </div>
              </button>
              {/* Dropdown Menu */}
              {isProfileMenuOpen && (
                <div className="absolute right-0 mt-2 w-48 bg-white border border-gray-200 rounded-lg shadow-lg z-50"
                  onMouseEnter={handleDropdownMouseEnter}
                  onMouseLeave={handleDropdownMouseLeave}
                >
                  <ul className="py-2 text-gray-700 text-sm">
                    <li>
                      <Link
                        to="/my-bookings"
                        className="block px-4 py-2 hover:bg-gray-100"
                        onClick={() => handleLinkClick("/my-bookings")}
                      >
                        My Bookings
                      </Link>
                    </li>
                    <li className="border-t border-gray-200">
                      <SignOutButton onClick={() => { toggleProfileMenu(); handleSignOut(); }} />
                    </li>
                  </ul>
                </div>
              )}
            </div>
          ) : (
            <Link to="/sign-in" style={{
              display: 'flex',
              alignItems: 'right',
              color: '#d90062',
              fontWeight: 'normal',
              marginLeft: '7rem'

            }}>
              <img src='/featureIcons/user2.svg' alt="Sign In" width={21} height={21} />
            </Link>
          )}
        </div>

        {!isDesktop && (
          <button
            style={{ display: isDesktop ? 'none' : 'block' }}
            onClick={() => setIsMenuOpen(!isMenuOpen)}
          >
            <img src={MenuIcon} alt="Menu" style={{ width: '2rem', height: '2rem' }} />
          </button>
        )}

        {/* Mobile Menu */}
        {isMenuOpen && (
          <div
            className="fixed inset-0 bg-black bg-opacity-50 z-40"
            onClick={() => setIsMenuOpen(false)}
          />
        )}
        <div
          className={`fixed top-0 left-0 w-80 h-full bg-white shadow-md transition-transform duration-300 ease-in-out z-40 overflow-y-scroll ${isMenuOpen ? 'transform-none' : '-translate-x-full'
            }`}
        >
          <MobileNavbar />
        </div>
        <div style={{ display: isDesktop ? 'flex' : 'none' }}>
          <span style={{ marginRight: '5rem' }}>
            <Navbar />
          </span>

          {/* User Profile Dropdown */}
          {isLoading ? (
            <div className="animate-pulse w-8 h-8 rounded-full bg-gray-300"></div> // Loading placeholder
          ) : isLoggedIn ? (
            <div className="relative"
              onMouseEnter={handleMouseEnter}
              onMouseLeave={handleMouseLeave}
            >
              <button
                onClick={toggleProfileMenu}
                className="flex items-center space-x-2 focus:outline-none mr-[2.5rem]"
              >
                <div className="relative flex items-center justify-center w-8 h-8 rounded-full text-white font-medium text-lg avatar" style={{ backgroundColor }}>
                  {displayName?.charAt(0) || "U"}
                  <span className="absolute top-0 right-0 w-2.5 h-2.5 bg-green-500 border-2 border-white rounded-full"></span>
                </div>
                <span className="text-gray-700 font-medium">
                  {displayName}
                </span>
              </button>
              {/* Dropdown Menu */}
              {isProfileMenuOpen && (
                <div className="absolute right-0 mt-2 w-48 bg-white border border-gray-200 rounded-lg shadow-lg z-50"
                  onMouseEnter={handleDropdownMouseEnter}
                  onMouseLeave={handleDropdownMouseLeave}
                >
                  <ul className="py-2 text-gray-700 text-sm">
                    <li>
                      <Link
                        to="/my-bookings"
                        className="block px-4 py-2 hover:bg-gray-100"
                        onClick={() => handleLinkClick("/my-bookings")}
                      >
                        My Bookings
                      </Link>
                    </li>
                    {user?.isAdmin && (
                      <>
                        <li>
                          <Link
                            to="/my-hotels"
                            className="block px-4 py-2 hover:bg-gray-100"
                            onClick={() => handleLinkClick("/my-hotels")}
                          >
                            My Hotels
                          </Link>
                        </li>
                        <li>
                          <Link
                            to="/my-blogs"
                            className="block px-4 py-2 hover:bg-gray-100"
                            onClick={() => handleLinkClick("/my-blogs")}
                          >
                            My Blogs
                          </Link>
                        </li>
                      </>
                    )}
                    <li className="border-t border-gray-200">
                      <SignOutButton onClick={() => { toggleProfileMenu(); handleSignOut(); }} />
                    </li>
                  </ul>
                </div>
              )}
            </div>
          ) : (
            <Link to="/sign-in" style={{
              display: 'flex',
              alignItems: 'center',
              color: '#d90062',
              fontWeight: 'bold',
              marginRight: '2.5rem',

            }}>
              Sign In
            </Link>
          )}
        </div>

      </div>
    </div>
  );
};

export default Header;
